import { type CSSProperties, Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  encodeTraxColumn,
  groupTraxMoves,
  type Side,
  type TraxMove,
  type TraxSlash,
  type TraxState,
  type TraxTile,
  traxCellKey,
  traxOutcomeLabel,
  traxSymbol,
  traxTileAt,
} from '../../games/trax';
import { traxCandidateGeometry, traxWinningLines } from '../../games/trax-visual';
import { contentKey } from '../../lib/content-key';
import { getEngine } from '../../wasm';
import './TraxBoard.css';

interface Props {
  state: TraxState;
  legal?: TraxMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (move: TraxMove) => void;
  mini?: boolean;
  lastMove?: TraxMove;
  staticView?: boolean;
  suppressTurnStatus?: boolean;
}

const TILE_PATHS: Record<TraxTile, [string, string]> = {
  WhiteNS: ['M50 0V100', 'M0 50H100'],
  WhiteEW: ['M0 50H100', 'M50 0V100'],
  WhiteNE: ['M50 0A50 50 0 0 0 100 50', 'M50 100A50 50 0 0 0 0 50'],
  WhiteES: ['M100 50A50 50 0 0 0 50 100', 'M0 50A50 50 0 0 0 50 0'],
  WhiteSW: ['M50 100A50 50 0 0 0 0 50', 'M50 0A50 50 0 0 0 100 50'],
  WhiteWN: ['M0 50A50 50 0 0 0 50 0', 'M100 50A50 50 0 0 0 50 100'],
};
const TILE_LABELS: Record<TraxTile, string> = {
  WhiteNS: '白は上下・赤は左右', WhiteEW: '白は左右・赤は上下',
  WhiteNE: '白は上右・赤は下左', WhiteES: '白は右下・赤は左上',
  WhiteSW: '白は下左・赤は上右', WhiteWN: '白は左上・赤は右下',
};

function TileLines({ tile }: { tile: TraxTile }) {
  const [white, red] = TILE_PATHS[tile];
  return (
    <>
      <rect width="100" height="100" fill="#252421" />
      <path d={red} stroke="#e6543e" strokeWidth="18" fill="none" />
      {/* Dark outline keeps both lines legible where a straight tile crosses. */}
      <path d={white} stroke="#252421" strokeWidth="24" fill="none" />
      <path d={white} stroke="#fffdf7" strokeWidth="18" fill="none" />
    </>
  );
}

function TilePicture({ tile }: { tile: TraxTile }) {
  return <svg className="trax-tile-picture" viewBox="0 0 100 100" aria-hidden="true"><TileLines tile={tile} /></svg>;
}

function WinningPath({ tile, side }: { tile: TraxTile; side: Side }) {
  return <path d={TILE_PATHS[tile][side === 'First' ? 0 : 1]} stroke="#f4c85b" strokeWidth="7" fill="none" />;
}

function BoundsEdges({ width, height, mini, staticView }: { width: number; height: number; mini?: boolean; staticView?: boolean }) {
  const w = width * 100, h = height * 100;
  // The caller supplies the displayed rectangle: confirmed, or speculative including every forced drop.
  const unit = mini ? Math.max(w, h) : 100;
  const widthOffset = mini ? unit * .23 : staticView ? 56 : 26;
  const heightOffset = mini ? unit * .23 : 26;
  const fontSize = mini ? unit * .22 : 23;
  const dimensions = [
    { axis: 'width', span: width, color: width >= 8 ? '#e7b52b' : '#77756d', length: w, cap: mini ? Math.min(unit * .065, w * .12) : 10, arrow: mini ? Math.min(unit * .055, w * .2) : 9, transform: `translate(0 ${-widthOffset})` },
    { axis: 'height', span: height, color: height >= 8 ? '#e7b52b' : '#aaa69b', length: h, cap: mini ? Math.min(unit * .065, h * .12) : 10, arrow: mini ? Math.min(unit * .055, h * .2) : 9, transform: `translate(${w + heightOffset} 0) rotate(90)` },
  ] as const;
  return <>
    <path data-testid="trax-bounds-width" data-span={width} data-threshold={width >= 8 ? 'met' : 'below'} d={`M0 0H${w} M0 ${h}H${w}`} stroke={width >= 8 ? '#e7b52b' : '#77756d'} strokeWidth={mini ? 1.1 : 2.5} vectorEffect="non-scaling-stroke" fill="none" />
    <path data-testid="trax-bounds-height" data-span={height} data-threshold={height >= 8 ? 'met' : 'below'} d={`M0 0V${h} M${w} 0V${h}`} stroke={height >= 8 ? '#e7b52b' : '#aaa69b'} strokeWidth={mini ? 1.1 : 2.5} vectorEffect="non-scaling-stroke" fill="none" />
    {dimensions.map(({ axis, span, color, length, cap, arrow, transform }) => <g key={axis} className="trax-dimension" data-testid={`trax-dimension-${axis}`} data-span={span} data-threshold={span >= 8 ? 'met' : 'below'} transform={transform} fill={color} stroke={color}>
      <path d={`M0 ${-cap}V${cap} M${length} ${-cap}V${cap} M0 0H${length} M${arrow} ${-arrow / 2}L0 0L${arrow} ${arrow / 2} M${length - arrow} ${-arrow / 2}L${length} 0L${length - arrow} ${arrow / 2}`} fill="none" strokeWidth={mini ? .8 : 1.2} vectorEffect="non-scaling-stroke" />
      <text className="trax-dimension-label" x={mini ? Math.max(length / 2, fontSize * .95) : length / 2} y="0" fontSize={fontSize} textAnchor="middle" dominantBaseline="central" strokeWidth={fontSize * .32} data-testid={`trax-dimension-${axis}-label`}>{span}/8</text>
    </g>)}
  </>;
}

export function TraxBoard({ state, legal, yourSide, myTurnAllowed, onMove, mini, staticView }: Props) {
  const isMyTurn = myTurnAllowed ?? (yourSide != null && yourSide === state.side);
  const canPlay = !mini && !staticView && isMyTurn && state.outcome == null && onMove != null;
  const grouped = useMemo(() => groupTraxMoves(legal ?? []), [legal]);
  const [selectedCell, setSelectedCell] = useState<{ col: number; row: number } | null>(null);
  const [selectedShape, setSelectedShape] = useState<TraxSlash | null>(null);
  const submittedStateRef = useRef<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const candidatesRef = useRef<HTMLDivElement>(null);
  const winning = useMemo(() => traxWinningLines(state), [state]);
  const stateKey = useMemo(() => contentKey(state), [state]);

  // Content equality preserves a selection across broadcasts from another board.
  // biome-ignore lint/correctness/useExhaustiveDependencies: changes to stateKey invalidate the pre-move coordinates.
  useEffect(() => {
    setSelectedCell(null);
    setSelectedShape(null);
    submittedStateRef.current = null;
  }, [stateKey]);

  const empty = state.width === 0 || state.height === 0;
  const border = mini || staticView ? 0 : 1;
  const selectedMoves = useMemo(() => selectedCell
    ? grouped.get(traxCellKey(selectedCell.col, selectedCell.row)) ?? []
    : [], [grouped, selectedCell]);
  // The actual engine owns forced placement. Never apply to the live state:
  // speculative states are disposable and never reach onMove/history/storage.
  const candidates = useMemo(() => {
    if (!canPlay || !selectedCell) return [];
    return selectedMoves.flatMap((move) => {
      try {
        const next = getEngine('Trax').apply(structuredClone(state), move) as TraxState;
        const chosen = next.last_drop[0];
        if (!chosen) return [];
        const tile = traxTileAt(next, chosen[0], chosen[1]);
        if (!tile) return [];
        // last_drop is rebased to the NEW rectangle; moves use the OLD one.
        const dx = (empty ? 0 : move.col - 1) - chosen[0];
        const dy = (empty ? 0 : move.row - 1) - chosen[1];
        const ghosts = next.last_drop.flatMap(([x, y], index) => {
          const placed = traxTileAt(next, x, y);
          return placed ? [{ col: empty ? 0 : x + dx + 1, row: empty ? 0 : y + dy + 1, tile: placed, index }] : [];
        });
        return [{ move, tile, ghosts, bounds: { minCol: empty ? 0 : dx + 1, minRow: empty ? 0 : dy + 1, width: next.width, height: next.height }, geometry: traxCandidateGeometry(state, move, tile) }];
      } catch {
        // A stale/inconsistent legal prop must not expose a contradictory chain.
        return [];
      }
    });
  }, [canPlay, selectedCell, selectedMoves, state, empty]);
  const chosenCandidate = candidates.find(({ move }) => move.tile === selectedShape);
  const selectedMove = chosenCandidate?.move;
  const showPicker = canPlay && selectedCell !== null && candidates.length > 0;
  // An unchosen orientation is a candidate, never an implicit move.
  const preview = showPicker ? chosenCandidate : null;
  const measured = preview?.bounds ?? { minCol: 1, minRow: 1, width: state.width, height: state.height };
  const ghosts = new Map(preview?.ghosts.map((ghost) => [traxCellKey(ghost.col, ghost.row), ghost]) ?? []);
  const minCol = empty ? 0 : Math.min(1 - border, ...Array.from(ghosts.values(), (g) => g.col));
  const minRow = empty ? 0 : Math.min(1 - border, ...Array.from(ghosts.values(), (g) => g.row));
  const maxCol = empty ? 0 : Math.max(state.width + border, ...Array.from(ghosts.values(), (g) => g.col));
  const maxRow = empty ? 0 : Math.max(state.height + border, ...Array.from(ghosts.values(), (g) => g.row));
  const cols = maxCol - minCol + 1;
  const rows = maxRow - minRow + 1;
  const lastDrops = new Map((state.last_drop ?? []).map(([x, y], index) => [traxCellKey(x, y), index]));

  useLayoutEffect(() => {
    if (!showPicker || !selectedCell) return;
    const anchor = gridRef.current?.querySelector<HTMLButtonElement>(`[data-testid="trax-cell-${selectedCell.col}-${selectedCell.row}"]`);
    anchor?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' });
  }, [showPicker, selectedCell]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new speculative chain changes the collision rectangles.
  useLayoutEffect(() => {
    const layer = candidatesRef.current, frame = frameRef.current;
    if (!showPicker || !selectedCell || !layer || !frame) return;
    const anchor = gridRef.current?.querySelector<HTMLButtonElement>(`[data-testid="trax-cell-${selectedCell.col}-${selectedCell.row}"]`);
    if (!anchor) return;
    const place = () => {
      const a = anchor.getBoundingClientRect(), f = frame.getBoundingClientRect();
      const container = frame.closest('.play-active') ?? frame.querySelector('.trax-viewport') ?? frame;
      const bounds = container.getBoundingClientRect();
      const left = Math.max(8, bounds.left + 4), right = Math.min(innerWidth - 8, bounds.right - 4);
      const top = Math.max(8, bounds.top + 4), bottom = Math.min(innerHeight - 8, bounds.bottom - 4);
      const cx = a.left + a.width / 2, cy = a.top + a.height / 2;
      const blocked = Array.from(frame.querySelectorAll('.trax-cell.occupied, .trax-cell.selected, [data-preview-tile], .trax-dimension')).map(e => e.getBoundingClientRect());
      const placed: { left: number; right: number; top: number; bottom: number }[] = [];
      const buttons = Array.from(layer.querySelectorAll<HTMLButtonElement>('.trax-option'));
      // Curves first, then straight; the straight candidate keeps its forward position.
      buttons.sort((a, b) => Number(a.dataset.turn === 'straight') - Number(b.dataset.turn === 'straight'));
      for (const button of buttons) {
        const d = Number(button.dataset.exit), dx = [0, 1, 0, -1][d], dy = [-1, 0, 1, 0][d];
        const size = 44, base = Math.max(a.width, a.height) / 2 + size / 2 + 3;
        let best: { x: number; y: number; score: number } | null = null;
        for (let distance = base; distance <= Math.max(right - left, bottom - top); distance += 6) {
          for (let tangent = -Math.min(distance * .65, 96); tangent <= Math.min(distance * .65, 96); tangent += 6) {
            const x = cx + dx * distance - dy * tangent - size / 2;
            const y = cy + dy * distance + dx * tangent - size / 2;
            if (x < left || x + size > right || y < top || y + size > bottom) continue;
            const rect = {left:x, right:x+size, top:y, bottom:y+size};
            if ([...blocked, ...placed].some(r => rect.left < r.right + 2 && rect.right > r.left - 2 && rect.top < r.bottom + 2 && rect.bottom > r.top - 2)) continue;
            const score = distance - base + Math.abs(tangent) * 1.5;
            if (!best || score < best.score) best = { x, y, score };
          }
        }
        // The selected canvas has a free ring; the clamp also handles a tiny embedded view.
        const x = best?.x ?? Math.max(left, Math.min(cx + dx * base - size / 2, right - size));
        const y = best?.y ?? Math.max(top, Math.min(cy + dy * base - size / 2, bottom - size));
        button.style.left = `${x - f.left}px`;
        button.style.top = `${y - f.top}px`;
        placed.push({left:x, right:x+size, top:y, bottom:y+size});
      }
    };
    place(); const raf = requestAnimationFrame(place);
    const observer = new ResizeObserver(place); observer.observe(anchor); observer.observe(frame);
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { cancelAnimationFrame(raf); observer.disconnect(); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [showPicker, selectedCell, chosenCandidate]);
  const hasCoords = !mini;
  const gridStyle: CSSProperties = {
    gridTemplateColumns: `${hasCoords ? '24px ' : ''}repeat(${cols}, minmax(0, 1fr))`,
    width: mini ? '100%' : `${24 + cols * 56}px`,
    minWidth: mini ? undefined : `${24 + cols * 44}px`,
  };

  function commit(move: TraxMove) {
    if (!canPlay || submittedStateRef.current === stateKey) return;
    submittedStateRef.current = stateKey;
    onMove?.(move);
    setSelectedCell(null);
    setSelectedShape(null);
  }

  function selectCell(col: number, row: number) {
    if (!canPlay) return;
    const moves = grouped.get(traxCellKey(col, row));
    if (!moves?.length) return;
    if (selectedCell?.col === col && selectedCell.row === row) {
      if (selectedMove) { commit(selectedMove); return; }
      setSelectedCell(null);
      setSelectedShape(null);
      return;
    }
    setSelectedCell({ col, row });
    setSelectedShape(null);
  }

  if (mini) {
    const margin = Math.max(1, state.width, state.height) * 46;
    return (
      <div className="trax-frame mini" data-testid="trax-board" data-width={state.width} data-height={state.height} data-plies={state.plies}>
        <svg className="trax-thumbnail" viewBox={`0 ${-margin} ${Math.max(1, state.width) * 100 + margin} ${Math.max(1, state.height) * 100 + margin}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`トラックス ${state.width}列 ${state.height}行`}>
          {state.cells.map((tile, i) => tile ? (
            <g key={i} transform={`translate(${(i % state.width) * 100} ${Math.floor(i / state.width) * 100})`} data-testid="trax-mini-tile" data-x={i % state.width} data-y={Math.floor(i / state.width)} data-tile={tile} data-winning-line={winning.cells.has(traxCellKey(i % state.width, Math.floor(i / state.width))) ? state.outcome?.winner : undefined}>
              <TileLines tile={tile} />
              {winning.cells.has(traxCellKey(i % state.width, Math.floor(i / state.width))) && state.outcome?.winner && <WinningPath tile={tile} side={state.outcome.winner} />}
            </g>
          ) : null)}
          {!empty && <g className="trax-mini-bounds" data-testid="trax-bounds" data-width={state.width} data-height={state.height} data-measure-kind="confirmed"><BoundsEdges width={state.width} height={state.height} mini /></g>}
        </svg>
      </div>
    );
  }

  return (
    <div ref={frameRef} className={`trax-frame${showPicker ? ' choosing' : ''}`} data-testid="trax-board" data-own-line={yourSide === 'First' ? 'white' : yourSide === 'Second' ? 'red' : undefined}>
      {!mini && state.outcome && (
        <div className="trax-status" data-testid="trax-status" aria-live="polite">
          {traxOutcomeLabel(state.outcome)}
        </div>
      )}
      {showPicker && <div ref={candidatesRef} className="trax-candidates" data-testid="trax-candidates" role="group" aria-label="接続方向のタイル候補">
        {candidates.map(({ move, tile, geometry }) => <button
          type="button" key={move.tile}
          className={`trax-option${selectedShape === move.tile ? ' selected' : ''}`}
          aria-label={`${traxSymbol(move.tile)}の向き、${TILE_LABELS[tile]}、同じ候補または元のマスを再タップで配置`}
          aria-pressed={selectedShape === move.tile}
          onClick={event => { event.stopPropagation(); if (selectedShape === move.tile) commit(move); else setSelectedShape(move.tile); }}
          data-testid={`trax-shape-${move.tile}`}
          data-entry={geometry.entry} data-color={geometry.color} data-exit={geometry.exit} data-turn={geometry.turn}
        ><TilePicture tile={tile} /></button>)}
      </div>}
      <div className={`trax-viewport${staticView ? ' static-view' : ''}`} tabIndex={mini ? undefined : 0} aria-label="Trax盤。広い盤は横と縦にスクロールできます">
        <div className="trax-grid" ref={gridRef} style={gridStyle} data-testid="trax-grid">
          {measured.width > 0 && measured.height > 0 && <svg className="trax-bounds" viewBox={`0 0 ${measured.width * 100} ${measured.height * 100}`} preserveAspectRatio="none" style={{ gridColumn: `${(hasCoords ? 1 : 0) + 1 + measured.minCol - minCol} / span ${measured.width}`, gridRow: `${(hasCoords ? 1 : 0) + 1 + measured.minRow - minRow} / span ${measured.height}` }} data-testid="trax-bounds" data-width={measured.width} data-height={measured.height} data-measure-kind={preview ? "preview" : "confirmed"} role="img" aria-label={`${preview ? "仮置きと強制配置を含む盤" : "確定盤"}の幅${measured.width}、高さ${measured.height}。各方向8マス以上は黄。ライン勝利とは別`}>
            <BoundsEdges width={measured.width} height={measured.height} staticView={staticView} />
          </svg>}
          {hasCoords && <span className="trax-coordinate" aria-hidden="true" />}
          {hasCoords && Array.from({ length: cols }, (_, c) => (
            <span className="trax-coordinate" key={`col-${c}`} aria-hidden="true">
              {encodeTraxColumn(c + minCol)}
            </span>
          ))}
          {Array.from({ length: rows }, (_, r) => (
            <Fragment key={`row-${r}`}>
              {hasCoords && <span className="trax-coordinate" aria-hidden="true">{r + minRow}</span>}
              {Array.from({ length: cols }, (_, c) => {
                const col = c + minCol;
                const row = r + minRow;
                const x = col - 1;
                const y = row - 1;
                const tile = empty ? null : traxTileAt(state, x, y);
                const available = canPlay && grouped.has(traxCellKey(col, row));
                const selected = canPlay && selectedCell?.col === col && selectedCell.row === row;
                const dropIndex = !mini ? lastDrops.get(traxCellKey(x, y)) : undefined;
                const ghost = ghosts.get(traxCellKey(col, row));
                const coordinate = `${encodeTraxColumn(col)}${row}`;
                const tileLabel = tile ? `、${TILE_LABELS[tile]}` : available ? '、配置可能' : '、配置不可';
                return (
                  <button
                    type="button"
                    key={traxCellKey(col, row)}
                    className={`trax-cell${tile ? ' occupied' : ''}${available ? ' legal' : ''}${selected ? ' selected' : ''}${dropIndex === 0 ? ' last-drop' : ''}${dropIndex !== undefined && dropIndex > 0 ? ' forced-drop' : ''}${winning.cells.has(traxCellKey(x, y)) ? ' winning-line' : ''}`}
                    disabled={!available}
                    aria-label={`${coordinate}${tileLabel}${selected ? selectedMove ? '、再タップで配置' : '、選択中、再タップで解除' : ''}${dropIndex !== undefined && dropIndex > 0 ? '、強制配置' : ''}`}
                    aria-pressed={selected}
                    onClick={() => selectCell(col, row)}
                    data-testid={`trax-cell-${col}-${row}`}
                    data-col={col}
                    data-row={row}
                    data-tile={tile ?? ''}
                    data-preview-tile={ghost?.tile}
                    data-preview-drop={ghost ? ghost.index === 0 ? 'chosen' : 'forced' : undefined}
                    data-preview-index={ghost?.index}
                    data-drop={dropIndex === 0 ? 'chosen' : dropIndex !== undefined ? 'forced' : undefined}
                    data-winning-line={winning.cells.has(traxCellKey(x, y)) ? state.outcome?.winner : undefined}
                  >
                    {tile && winning.cells.has(traxCellKey(x, y)) && state.outcome?.winner && <svg className="trax-winning-path" viewBox="0 0 100 100" aria-hidden="true"><WinningPath tile={tile} side={state.outcome.winner} /></svg>}
                    {tile ? <TilePicture tile={tile} /> : ghost ? <span className="trax-preview" aria-hidden="true"><TilePicture tile={ghost.tile} /></span> : available ? <span className="trax-place-marker" aria-hidden="true">+</span> : null}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

    </div>
  );
}
